<script setup>
// WITTIA: settings section for a native Wittia WhatsApp inbox. Shows the webhook
// URL to register in the Wittia portal, lets the admin paste the webhook secret,
// rotate the API key, and resync templates. New file per fork discipline.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const webhookSecret = ref('');
const apiKey = ref('');
const isSyncingTemplates = ref(false);

const rules = { webhookSecret: { required }, apiKey: { required } };
const v$ = useVuelidate(rules, { webhookSecret, apiKey });

const updateProviderConfig = async patch => {
  await store.dispatch('inboxes/updateInbox', {
    id: props.inbox.id,
    formData: false,
    channel: {
      provider_config: {
        ...props.inbox.provider_config,
        ...patch,
      },
    },
  });
};

const updateWebhookSecret = async () => {
  try {
    await updateProviderConfig({ webhook_secret: webhookSecret.value });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const updateApiKey = async () => {
  try {
    await updateProviderConfig({ api_key: apiKey.value });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const syncTemplates = async () => {
  isSyncingTemplates.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSyncingTemplates.value = false;
  }
};
</script>

<template>
  <div>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WITTIA_WEBHOOK_URL_TITLE')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WITTIA_WEBHOOK_URL_SUBHEADER')"
    >
      <woot-code :script="inbox.callback_webhook_url" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WITTIA_WEBHOOK_SECRET_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WITTIA_WEBHOOK_SECRET_SUBHEADER')
      "
    >
      <div class="flex flex-1 justify-between items-center">
        <woot-input
          v-model="webhookSecret"
          type="text"
          class="flex-1 mr-2 [&>input]:!mb-0"
          :placeholder="
            $t('INBOX_MGMT.SETTINGS_POPUP.WITTIA_WEBHOOK_SECRET_PLACEHOLDER')
          "
        />
        <NextButton
          :disabled="v$.webhookSecret.$invalid"
          @click="updateWebhookSecret"
        >
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WITTIA_WEBHOOK_SECRET_BUTTON') }}
        </NextButton>
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
      "
    >
      <div class="flex flex-1 justify-between items-center">
        <woot-input
          v-model="apiKey"
          type="text"
          class="flex-1 mr-2 [&>input]:!mb-0"
          :placeholder="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER')
          "
        />
        <NextButton :disabled="v$.apiKey.$invalid" @click="updateApiKey">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON') }}
        </NextButton>
      </div>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
      </NextButton>
    </SettingsFieldSection>
  </div>
</template>
